import { cloneDeep, forEach, isEmpty } from 'lodash';
import moment from 'moment';
import { combineEpics, ofType } from 'redux-observable';
import { forkJoin, iif, of } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { filter, map, mapTo, mergeMap, switchMap, tap } from 'rxjs/operators';

import { dictionariesRequest, dictionaryIds } from '@dict';
import { isCurrentPageSel } from '@location';
import { getCustomErrorMessage } from '@modules/bulkEntry/duck/utils';
import {
  APPROVE_ADVANCEMENTS_RESPONSE,
  RECORD_ADVANCEMENT_RESPONSE,
  UNAPPROVE_ADVANCEMENT_RESPONSE,
} from '@modules/progress/duck/actions';
import { START_ADVANCEMENT_RESPONSE } from '@modules/progress/startAdvancement/duck/actions';
import { unstartAdvancementResponse } from '@modules/progress/startAdvancement/duck/actionsTyped';
import { rosterRouteForOrg } from '@modules/roster-lite/duck/actions';
import { hasRosterLiteAccessSel } from '@modules/roster-lite/duck/selectors';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import {
  advancementStatuses,
  advancementTypes,
} from '@modules/shared/constants';
import {
  isParentOrYouthMemberSel,
  organizationGuidSel,
  urlUserIdSel,
} from '@modules/shared/duck/selectors';
import { intl } from '@modules/shared/localization';
import {
  APP_BOOTSTRAP_DONE,
  MEMBER_DETAILS_REFRESH,
  SET_ORGANIZATION,
  goToYouthProfile,
  setShouldMemberDetailsReload,
} from '@shared/duck/actions';
import { unitTypeIdSel } from '@shared/duck/selectors/general.selectors';
import { toastService } from '@toasts';
import { eligibleAdvancementTypesSel } from '@unit/duck/selectors';
import { userIdSel as loggedInUserIdSel } from '@user';
import { catchAndReport } from '@utils/rxjs/operators';

import { formatApiDateTime } from '../../../modules/progress/common/utils';
import { advancementHistoryRequest } from '../../advancement/advancementHistory';
import { packRosterRequest } from '../../advancement/common/actions';
import { youthsAndAdultsSel } from '../../progress/activities';
import { UPDATE_ADVANCEMENT_RESPONSE } from '../../progress/editAdvancement';
import { shouldScoutDetailsReloadSel } from '../../progress/unapproveAdvancement/duck/selectors';
import { imageUploadS3$ } from '../../shared/duck/services';
import { GET_YOUTH_REQUIREMENTS_AFTER_EDIT_ERROR } from '../constants';
import { getEagleHours } from '../utils';
// TODO: we need to remove this cyclic deps
import {
  EAGLE_ACTIVITY_REQUEST,
  EDIT_EAGLE_PROJECT_REQUEST,
  MEMBER_DETAILS_REQUEST,
  ROUTE_YOUTH_ADV_REQUIREMENTS,
  ROUTE_YOUTH_PROFILE,
  SAVE_YOUTH_DETAILS_REQUEST,
  SAVE_YOUTH_PROFILE_PICTURE_REQUEST,
  SET_EDIT_MODE,
  SUBMIT_EAGLE_PROJECT_REQUEST,
  UPDATE_ACTIVE_VERSION_REQUEST,
  YOUTH_ACTIVITIES_SUMMARY_REQUEST,
  YOUTH_ADVANCEMENTS_REQUEST,
  YOUTH_ADV_REQUIREMENTS_REQUEST,
  eagleActivityError,
  eagleActivityResponse,
  memberDetailsError,
  memberDetailsRequest,
  memberDetailsResponse,
  saveYouthDetailsError,
  saveYouthDetailsResponse,
  saveYouthProfilePictureError,
  saveYouthProfilePictureResponse,
  setEditYouthDetailSidebar,
  submitEagleProjectError,
  submitEagleProjectResponse,
  updateActiveVersionError,
  updateActiveVersionResponse,
  youthActivitySummaryError,
  youthActivitySummaryResponse,
  youthAdvRequirementsError,
  youthAdvRequirementsRequest,
  youthAdvRequirementsResponse,
  youthAdvancementsError,
  youthAdvancementsResponse,
} from './actions';
import {
  editAdvRqmtsError,
  editAdvRqmtsRequest,
  editAdvRqmtsResponse,
} from './actionsTyped';
import {
  eagleActivitySel,
  isAdvancementInYouthRecordSel,
  memberDetailsLoadedSel,
  memberDetailsSel,
  shouldMemberDetailsReloadSel,
  userIdSel,
  youthAdvRequirementsParamsSel,
  youthAdvRequirementsSel,
  youthInfoSel,
  youthPersonGuidSel,
  youthPersonIdSel,
  youthProfileInfoSel,
} from './selectors';
import { memberDetailsUserIdSel } from './selectorsTyped';
import services from './services';

const goToRosterEpic$ = (action$, state$) =>
  action$
    .skipUntil(action$.ofType(APP_BOOTSTRAP_DONE))
    .ofType(SET_ORGANIZATION)
    .filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_ADV_REQUIREMENTS))
    .map(() =>
      rosterRouteForOrg(
        hasRosterLiteAccessSel(state$.value),
        organizationGuidSel(state$.value),
      ),
    );

const fetchFormDictsEpic$ = action$ =>
  action$.pipe(
    ofType(MEMBER_DETAILS_REQUEST, SET_EDIT_MODE),
    map(() =>
      dictionariesRequest(
        dictionaryIds.STATES,
        dictionaryIds.PHONE_COUNTRIES,
        dictionaryIds.RANKS,
        dictionaryIds.MERIT_BADGES,
        dictionaryIds.AWARDS,
        dictionaryIds.ADVENTURES,
        dictionaryIds.SSELECTIVES,
      ),
    ),
  );

const loadMemberDetailsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(MEMBER_DETAILS_REQUEST, MEMBER_DETAILS_REFRESH),
    switchMap(() => {
      const state = state$.value;
      const memberDetails = memberDetailsSel(state);
      const userId = memberDetailsUserIdSel(state);
      const eligibleAdvancementTypes = eligibleAdvancementTypesSel(state);
      const selectedUnitTypeId = unitTypeIdSel(state);
      const unitTypeId =
        typeof selectedUnitTypeId === 'string'
          ? Number(selectedUnitTypeId)
          : selectedUnitTypeId;
      const isParentOrYouthMember = isParentOrYouthMemberSel(state);
      const isRefresh =
        shouldMemberDetailsReloadSel(state) ||
        shouldScoutDetailsReloadSel(state);
      const dataSource =
        isRefresh ||
        isEmpty(memberDetails) ||
        memberDetails.userId !== userId ||
        memberDetails.isParentOrYouthMember !== isParentOrYouthMember
          ? services.getYouthData$({
              userId,
              eligibleAdvancementTypes,
              unitTypeId,
              isParentOrYouthMember,
            })
          : of(memberDetails);

      return dataSource.pipe(
        mergeMap(res =>
          Observable.concat(
            of(memberDetailsResponse(res)),
            of(setShouldMemberDetailsReload(false)),
            of(setEditYouthDetailSidebar(false)),
          ),
        ),
        catchAndReport(err => of(memberDetailsError(err))),
      );
    }),
  );

const saveYouthProfilePicture$ = (action$, state$) =>
  action$
    .ofType(SAVE_YOUTH_PROFILE_PICTURE_REQUEST)
    .switchMap(({ payload }) => {
      const state = state$.value;
      const youthUserId = youthPersonIdSel(state);
      const personGuid = youthPersonGuidSel(state);
      const { imageFile } = payload;
      const imgType = imageFile.type;

      return forkJoin([
        services.saveProfilePicture$({ ...payload, personGuid }),
        services.getPreSignedUrl$(youthUserId, imgType).pipe(
          switchMap(response =>
            imageUploadS3$(youthUserId, imgType, payload, response),
          ),
          tap(() =>
            toastService.info(
              intl.formatMessage({
                id: 'profile.ProfileInfo.save.profileSaveDisclaimer',
              }),
            ),
          ),
          tap(() =>
            toastService.success(
              intl.formatMessage({
                id: 'profile.ProfileInfo.save.success',
              }),
            ),
          ),
          catchAndReport(err =>
            Observable.of(saveYouthProfilePictureError(err)),
          ),
        ),
      ]).pipe(
        mergeMap(() =>
          Observable.concat(
            of(
              esbApi.util.invalidateTags([
                { type: tagTypes.CompletePersonProfile, id: personGuid },
                {
                  type: tagTypes.UnitRoster,
                  id: `RosterYouth-${personGuid}`,
                },
              ]),
            ),
            of(memberDetailsRequest()),
            of(saveYouthProfilePictureResponse()),
            of(packRosterRequest()),
          ),
        ),
        catchAndReport(err => Observable.of(saveYouthProfilePictureError(err))),
      );
    });

const saveYouthDetails$ = (action$, state$) =>
  action$.ofType(SAVE_YOUTH_DETAILS_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const personGuid = youthPersonGuidSel(state);
    const userId = userIdSel(state);

    return forkJoin([services.updateSBProfile$(payload, userId)]).pipe(
      tap(() =>
        toastService.success(
          intl.formatMessage({
            id: 'profile.ProfileInfo.save.success',
          }),
        ),
      ),
      mergeMap(() =>
        Observable.concat(
          of(
            esbApi.util.invalidateTags([
              { type: tagTypes.CompletePersonProfile, id: personGuid },
            ]),
          ),
          of(memberDetailsRequest()),
          of(saveYouthDetailsResponse()),
        ),
      ),
      catchAndReport(err => Observable.of(saveYouthDetailsError(err))),
    );
  });

/** @deprecated */
const eagleActivityRequestEpic$ = action$ =>
  action$.pipe(
    ofType(EAGLE_ACTIVITY_REQUEST),
    switchMap(({ payload: id }) =>
      services.getEagleActivity$(id).pipe(
        map(eagleActivityResponse),
        catchAndReport(err => of(eagleActivityError(err))),
      ),
    ),
  );

const reloadYouthAdvancementsEpic$ = (action$, state$) =>
  action$.pipe(
    filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_PROFILE)),
    ofType(
      RECORD_ADVANCEMENT_RESPONSE,
      APPROVE_ADVANCEMENTS_RESPONSE,
      UNAPPROVE_ADVANCEMENT_RESPONSE,
    ),
    switchMap(() => {
      const state = state$.value;
      const userId = userIdSel(state);
      const eligibleAdvancementTypes = eligibleAdvancementTypesSel(state);
      const unitTypeId = unitTypeIdSel(state);
      const userData = memberDetailsSel(state);
      return services
        .getYouthAdvancements$({
          userId,
          userData,
          eligibleAdvancementTypes,
          unitTypeId,
        })
        .pipe(
          map(memberDetailsResponse),
          catchAndReport(err => of(memberDetailsError(err))),
        );
    }),
  );

const loadYouthAdvRequirementsEpic$ = (action$, state$, { store }) =>
  action$.pipe(
    ofType(YOUTH_ADV_REQUIREMENTS_REQUEST),
    filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_ADV_REQUIREMENTS)),
    switchMap(({ payload, forceRefetch }) => {
      const state = state$.value;
      const youthAdvReqsParams = youthAdvRequirementsParamsSel(state);
      const userId = userIdSel(state);
      const { advancementType, advancementId } = youthAdvReqsParams;
      const isAdvancementInYouthRecord = isAdvancementInYouthRecordSel(state);
      const memberDetailsLoaded = memberDetailsLoadedSel(state);
      const memberDetails = memberDetailsSel(state);
      const omitYouthRequirement =
        memberDetailsLoaded &&
        memberDetails.userId === userId &&
        payload?.pageLoad &&
        !isAdvancementInYouthRecord;

      if (!advancementId || !userId) {
        return of(
          youthAdvRequirementsError({
            message: `Invalid advancement id ${advancementId}`,
            userId,
            advancementType,
            advancementId,
            omitYouthRequirement,
          }),
        );
      }

      return services
        .getYouthAdvRequirements$({
          userId,
          advancementType,
          advancementId,
          omitYouthRequirement,
          dispatch: store.dispatch,
          forceRefetch,
        })
        .pipe(
          mergeMap(reqResponse => {
            const actions = [of(youthAdvRequirementsResponse(reqResponse))];

            return Observable.concat(...actions);
          }),
          catchAndReport(err => of(youthAdvRequirementsError(err))),
        );
    }),
  );

const getYouthAdvRankRequirementsEpic$ = (action$, state$, { store }) =>
  action$.pipe(
    ofType(YOUTH_ADV_REQUIREMENTS_REQUEST),
    filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_PROFILE)),
    switchMap(({ payload, forceRefetch }) => {
      const state = state$.value;
      const userId = userIdSel(state);
      const { rankId } = payload;

      if (!rankId) {
        return of(
          youthAdvRequirementsError({ message: `Invalid rank id ${rankId}` }),
        );
      }
      return services
        .getYouthAdvRequirements$({
          userId,
          advancementId: rankId,
          advancementType: advancementTypes.RANKS,
          dispatch: store.dispatch,
          forceRefetch,
        })
        .pipe(
          map(youthAdvRequirementsResponse),
          catchAndReport(err => of(youthAdvRequirementsError(err))),
        );
    }),
  );

const updateActiveVersionEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_ACTIVE_VERSION_REQUEST),
    switchMap(({ payload }) => {
      const state = state$.value;
      const versionId = Number(payload);
      const userId = urlUserIdSel(state);
      const { advancementType, id, status } = youthAdvRequirementsSel(state);
      const organizationGuid = organizationGuidSel(state);

      return iif(
        () =>
          advancementType === advancementTypes.MERIT_BADGES &&
          status === advancementStatuses.NOT_STARTED,
        // new merit badges need to be started in order to set version (can be done simultaneously)
        services.startYouthMeritBadge$({
          userId,
          organizationGuid,
          id: `${id}`,
          versionId,
          dateStarted: moment().format('YYYY-MM-DD'),
        }),

        // for all other cases we can just set version directly.
        services.updateActiveVersion$({
          userId,
          advancementType,
          advancementId: id,
          versionId,
        }),
      ).pipe(
        mergeMap(() =>
          Observable.concat(
            of(
              esbApi.util.invalidateTags([
                tagFactory.AwardUserRequirements.orgRequirements(
                  organizationGuid,
                  // award id
                  id,
                ),
              ]),
            ),
            of(updateActiveVersionResponse()),
            of(youthAdvRequirementsRequest({ versionId, forceRefetch: true })),
          ),
        ),
        catchAndReport(err => of(updateActiveVersionError(err))),
      );
    }),
  );

const editAdvRqmtsEpic$ = (action$, state$, { store }) =>
  action$.pipe(
    ofType(editAdvRqmtsRequest.type),
    filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_ADV_REQUIREMENTS)),
    switchMap(({ payload }) => {
      const { requirements, omitOrganizationGuid = false, onSuccess } = payload;
      const state = state$.value;
      const { userId, advancementType, advancementId } =
        youthAdvRequirementsParamsSel(state);
      const loggedInUserId = loggedInUserIdSel(state);
      const organizationGuid = organizationGuidSel(state);
      const invalidateTags = [];

      if (advancementType === advancementTypes.MERIT_BADGES) {
        invalidateTags.push(
          tagFactory.MeritBadgeUserRequirements.byMeritBadge(
            organizationGuid,
            advancementId,
          ),
        );
      }

      if (advancementType === advancementTypes.RANKS) {
        invalidateTags.push(
          tagFactory.OrganizationRoster.organizationRosters(organizationGuid),
        );
        invalidateTags.push({
          id: organizationGuid,
          type: tagTypes.UnitRoster,
        });
      }

      return services
        .editAdvRequirements$({
          userId: +userId,
          advancementType,
          advancementId,
          requirements,
          loggedInUserId,
          organizationGuid: omitOrganizationGuid ? null : organizationGuid,
          context: editAdvRqmtsRequest.type,
          dispatch: store.dispatch,
          forceRefetch: true,
        })
        .pipe(
          tap(() => {
            toastService.success(
              intl.formatMessage(
                {
                  id: 'youthProfile.editAdvRequirements.update',
                },
                { count: requirements.length },
              ),
            );
            if (onSuccess) {
              onSuccess();
            }
          }),
          mergeMap(editResponse =>
            Observable.concat(
              of(esbApi.util.invalidateTags(invalidateTags)),
              of(editAdvRqmtsResponse(editResponse)),
            ),
          ),
          catchAndReport(err => {
            if (err.message === GET_YOUTH_REQUIREMENTS_AFTER_EDIT_ERROR) {
              // Reload requirements to refresh status if it failed after editing the requirements
              return Observable.concat(
                of(youthAdvRequirementsRequest({ forceRefetch: true })),
                of(editAdvRqmtsError(err)),
              );
            }
            toastService.error(getCustomErrorMessage(err.response));
            return of(editAdvRqmtsError(err));
          }),
        );
    }),
  );

const reloadYouthAdvRequirementsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(
      START_ADVANCEMENT_RESPONSE,
      unstartAdvancementResponse.type,
      UPDATE_ADVANCEMENT_RESPONSE,
    ),
    filter(() => isCurrentPageSel(state$.value, ROUTE_YOUTH_ADV_REQUIREMENTS)),
    mapTo(youthAdvRequirementsRequest({ forceRefetch: true })),
  );

/** @deprecated Migrated to RTK */
const getYouthActivitiesSummary$ = action$ =>
  action$.pipe(
    ofType(YOUTH_ACTIVITIES_SUMMARY_REQUEST),
    switchMap(({ payload: userId }) =>
      services.getPersonalActivitiesSummary$(userId).pipe(
        map(summaryInfo => youthActivitySummaryResponse(summaryInfo)),
        catchAndReport(err => of(youthActivitySummaryError(err))),
      ),
    ),
  );

/** @deprecated */
const submitEagleProjectEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(SUBMIT_EAGLE_PROJECT_REQUEST),
    switchMap(({ payload }) => {
      const state = state$.value;
      const organizationGuid = organizationGuidSel(state);
      const { youths, adults } = youthsAndAdultsSel(state);
      const { profile } = youthProfileInfoSel(state);

      const timeActivityValues = getEagleHours(payload.totalHours);

      const registeredAdults = adults.map(adult => ({
        userId: adult.userId,
        organizationGuid,
        activityValues: [...timeActivityValues],
      }));
      const registeredYouths = youths.map(youth => ({
        userId: youth.userId,
        organizationGuid,
        activityValues: [...timeActivityValues],
      }));
      const {
        categoryId,
        startDate,
        endDate,
        specificGroupDescription,
        name,
        location,
        addressLine1,
        addressLine2,
        city,
        akelaStateId,
        zip5,
      } = payload;
      const data = {
        activityTypeId: 5,
        personGuid: profile.personGuid,
        categoryId,
        name,
        startDateTime: formatApiDateTime(startDate, !!payload.id),
        endDateTime: formatApiDateTime(endDate, !!payload.id),
        hostOrganizationGuid: organizationGuid,
        isEveryChildOrg: false,
        isPersonalActivity: true,
        organizationGuid,

        benefitGroup: specificGroupDescription,
        location: location ? location : undefined,
        addressLine1,
        addressLine2,
        city,
        akelaStateId: akelaStateId ? Number(akelaStateId) : akelaStateId,
        zip5,
        registeredAdults,
        registeredYouths,
      };
      return services.createEagleActivity$(data).pipe(
        tap(() => {
          toastService.success(
            intl.formatMessage({
              id: 'youthProfile.createEagleProject',
            }),
          );
        }),
        mergeMap(() => {
          const { userId } = youthInfoSel(state);
          return Observable.concat(
            of(submitEagleProjectResponse),
            of(setShouldMemberDetailsReload(true)),
            of(goToYouthProfile(userId)),
            of(advancementHistoryRequest()),
          );
        }),
        catchAndReport(err => of(submitEagleProjectError(err))),
      );
    }),
  );

/** @deprecated */
const editEagleParticipants$ = (
  eagleActivityId,
  organizationGuid,
  timeActivityValues = [],
  prevParticipants = [],
) => {
  const updatedParticipants = prevParticipants.map(participant => {
    const updatedActivityValues = participant.activityValues.map(
      activityObj => {
        const activityObjClone = cloneDeep(activityObj);
        const [hours] = timeActivityValues;

        if (activityObjClone.activityValueTypeId === 10) {
          activityObjClone.activityValue = hours.activityValue;
        }
        return activityObjClone;
      },
    );

    return {
      userId: participant.userId,
      organizationGuid,
      activityValues: updatedActivityValues,
      isDeclined: false,
      note: 'N/A',
    };
  });

  return services.updateEagleParticipants$(
    eagleActivityId,
    updatedParticipants,
  );
};

const editEagleProjectEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_EAGLE_PROJECT_REQUEST),
    switchMap(({ payload }) => {
      const state = state$.value;
      const { id, data } = payload;
      const organizationGuid = organizationGuidSel(state);
      const eagleActivity = eagleActivitySel(state);
      const timeActivityValues = getEagleHours(data.totalHours);
      const prevParticipants = eagleActivity.registeredAdults.concat(
        eagleActivity.registeredYouths,
      );

      const {
        categoryId,
        startDate,
        endDate,
        specificGroupDescription,
        name,
        location,
        addressLine1,
        addressLine2,
        city,
        akelaStateId,
        zip5,
      } = data;

      const body = {
        categoryId,
        startDateTime: formatApiDateTime(startDate, id),
        endDateTime: formatApiDateTime(endDate, id),
        name,

        benefitGroup: specificGroupDescription,
        location: location ? location : '',
        addressLine1: addressLine1 ? addressLine1 : '',
        addressLine2: addressLine2 ? addressLine2 : '',
        city: city ? city : '',
        akelaStateId: akelaStateId ? Number(akelaStateId) : null,
        zip5: zip5 ? zip5 : '',
      };
      return forkJoin([
        services.updateEagleActivity$(id, body),
        editEagleParticipants$(
          id,
          organizationGuid,
          timeActivityValues,
          prevParticipants,
        ),
      ]).pipe(
        tap(() => {
          toastService.success(
            intl.formatMessage({
              id: 'youthProfile.editEagleProject',
            }),
          );
        }),
        mergeMap(() => {
          const { userId } = youthInfoSel(state);
          return Observable.concat(
            of(submitEagleProjectResponse()),
            of(setShouldMemberDetailsReload(true)),
            of(goToYouthProfile(userId)),
            of(advancementHistoryRequest()),
          );
        }),
        catchAndReport(err => of(submitEagleProjectError(err))),
      );
    }),
  );

// Load youth advancements when changing organization after member details have loaded
const loadYouthAdvancementsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(YOUTH_ADVANCEMENTS_REQUEST),
    switchMap(() => {
      const state = state$.value;
      const userId = userIdSel(state);
      const eligibleAdvancementTypes = eligibleAdvancementTypesSel(state);
      const unitTypeId = unitTypeIdSel(state);

      return services
        .getYouthAdvancements$({
          userId,
          eligibleAdvancementTypes,
          unitTypeId,
        })
        .pipe(
          map(youthAdvancements => {
            const advancementsResponse = {};
            forEach(eligibleAdvancementTypes, type => {
              advancementsResponse[type] = youthAdvancements[type] || [];
            });
            return advancementsResponse;
          }),
          map(youthAdvancementsResponse),
          catchAndReport(err => of(youthAdvancementsError(err))),
        );
    }),
  );

export default combineEpics(
  goToRosterEpic$,
  eagleActivityRequestEpic$,
  fetchFormDictsEpic$,
  loadMemberDetailsEpic$,
  saveYouthDetails$,
  saveYouthProfilePicture$,
  reloadYouthAdvancementsEpic$,
  loadYouthAdvRequirementsEpic$,
  getYouthAdvRankRequirementsEpic$,
  editAdvRqmtsEpic$,
  reloadYouthAdvRequirementsEpic$,
  getYouthActivitiesSummary$,
  submitEagleProjectEpic$,
  editEagleProjectEpic$,
  updateActiveVersionEpic$,
  loadYouthAdvancementsEpic$,
);
